import { useState } from 'react';
// GAP: no DS icon for version history (History); lucide in both modes (design sheet §3.1, logged not substituted).
import { History, Pencil, Plus, Users } from 'lucide-react';
import { IconChip, StatusChip, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { Button } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useMacrosByProviderId } from '@/settings/services';
import { isDevHost } from '@/config/dev-host';
import { MacroImportDialog } from './macro-import-dialog';
import { useMacroRealtime } from '@/settings/realtime/use-macro-realtime';
import {
  formatDate,
  parseNoZIsoDatetime,
} from '@/shared/lib/date-format';
import { MacroEditDialog } from './macro-edit-dialog';
import { MacroHistoryDialog } from './macro-history-dialog';
import type {
  MacroCollaborator,
  MacroData,
} from '@/types/settings-api';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * Shared Macro tab — rendered by BOTH the Practitioner edit page
 * (`/settings/practitioner/:doctorSetupId`, Macro tab) and the Staff
 * edit page (`/settings/staff/:staffSetupId`, Macro tab — nurse-only).
 *
 * Flutter parity: `doctor_widget/tab_widget/macro_tab.dart` — the same
 * `MacroTabPage` widget is mounted by both `doctor_base_widget.dart`
 * and `staff_base_widget.dart`. We mirror that shared-page architecture
 * here: one React component, two call sites. PR #PROD-787 extracted this
 * from the previously-Practitioner-only `PractitionerMacroTab` to the
 * provider-agnostic shape and shared it across surfaces.
 *
 * **Why `providerId` + `organizationId` are both props (PROD-787)**
 *
 * The macros endpoint is keyed on a generic `providerId`. Server-side,
 * `doctorSetupId` (practitioners) and `staffSetupId` (nurse staff) share
 * the same `Providers` table — the GET is provider-agnostic. We accept
 * `providerId` directly instead of fetching via `usePractitionerById`
 * (which 404s for staff who aren't practitioners). `organizationId` is
 * lifted to a prop too — caller resolves from wherever fits the surface:
 *   - Practitioner page: `usePractitionerById(doctorSetupId).orgnizationId`
 *   - Staff page: `useAuthStore((s) => s.user.organizationId)`
 *
 * Wire shape verified against live dev cURL 2026-05-15:
 *
 *   GET /api/Doctor/GetMacrosByProviderId
 *     ?organizationId={orgId}&providerId={id}&search={q}
 *
 *   → MacroData[]   (bare array — no envelope)
 *
 * Each macro carries:
 *   - macroKey      — short label / trigger
 *   - macroValue    — STRINGIFIED Quill Delta JSON
 *   - macroValueFormatted — plaintext preview (used here for display)
 *   - ownerName / ownerPhoto — Azure-blob SAS URL with expiration
 *   - macroCollaborators[] — shared-access list
 *   - macroHistories[]     — version snapshots (rendered by MacroHistoryDialog)
 *   - isGlobal: bool       — org-wide vs private+collaborators
 *
 * Visibility on the Practitioner side is gated by OrgAdmin role at the
 * shell layer (Flutter `doctor_base_widget.dart:116-127
 * shouldShowAddMacro`). On the Staff side it's nurse-only per
 * `staff_base_widget.dart:144-156`. The tab itself does not check
 * permissions — if it renders, the user has access.
 */
export interface MacroTabProps {
  /**
   * Generic provider id — `doctorSetupId` for practitioners or
   * `staffSetupId` for nurses. Server stores both in the same Providers
   * table and the macros endpoint reads either transparently.
   */
  providerId: string;
  /**
   * Organisation id used as the `organizationId` query param on the
   * macros GET. Caller decides where to source from (see component
   * JSDoc for the two canonical resolutions).
   */
  organizationId: string | null;
}

export function MacroTab({ providerId, organizationId }: MacroTabProps) {
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 250);

  const { data, isLoading, error } = useMacrosByProviderId(
    providerId,
    organizationId,
    debouncedSearch,
  );

  // Subscribe to MacroUpdated events on the org notification hub. The
  // hook invalidates the macros-by-provider-id cache on every emit;
  // React Query re-fetches the current view. Mirrors Flutter
  // `macro_tab.dart:138-149`.
  useMacroRealtime(providerId);

  const macros = data ?? [];

  // Dialog state: when `editing` is null + open is true, we're in Add
  // mode. When editing is a MacroData, we're in Edit mode.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MacroData | null>(null);

  // PR-E: separate dialog for the history view + revert flow.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<MacroData | null>(null);

  // DEV-ONLY phrase import (spec §6.13). Rendered only on a dev host AND when this tab's own
  // add-macro condition holds (the org id has loaded); anywhere else it does not exist at all.
  const [importOpen, setImportOpen] = useState(false);
  const canImport = isDevHost() && !!organizationId;

  const handleAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const handleEdit = (macro: MacroData) => {
    setEditing(macro);
    setDialogOpen(true);
  };
  const handleHistory = (macro: MacroData) => {
    setHistoryTarget(macro);
    setHistoryOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className={SETTINGS_SECTION_TITLE}>
          Macros
        </h2>
        <Button
          type="button"
          variant="tertiary"
          data-slot="settings-add"
          onClick={handleAdd}
          disabled={!organizationId}
          className="text-brand-primary"
          title={
            organizationId
              ? undefined
              : 'Waiting for provider record to load'
          }
        >
          <Plus className="size-4 mr-2" />
          Add Macro
        </Button>
        {canImport && (
          <Button type="button" variant="tertiary" onClick={() => setImportOpen(true)}>
            Import phrases (dev)
          </Button>
        )}
      </div>

      <TextField
        value={searchInput}
        placeholder="Search macros by key…"
        onChange={(e) => setSearchInput(e.target.value)}
      />

      {isLoading ? (
        <BrandLoading size={48} label="Loading macros…" />
      ) : error ? (
        <p className="text-sm text-status-error">
          Failed to load macros. Please try again.
        </p>
      ) : macros.length === 0 ? (
        <p className="text-sm text-text-muted">
          {debouncedSearch
            ? `No macros match "${debouncedSearch}".`
            : 'No macros configured for this provider.'}
        </p>
      ) : (
        <ul className="space-y-2">
          {macros.map((macro) => (
            <MacroCard
              key={macro.id}
              macro={macro}
              onEdit={() => handleEdit(macro)}
              onHistory={() => handleHistory(macro)}
            />
          ))}
        </ul>
      )}

      {organizationId && (
        <MacroEditDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          initial={editing}
          addContext={{ ownerId: providerId, organizationId }}
          providerId={providerId}
        />
      )}

      {canImport && organizationId && (
        <MacroImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          providerId={providerId}
          organizationId={organizationId}
        />
      )}

      <MacroHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        macro={historyTarget}
        providerId={providerId}
      />
    </div>
  );
}

function MacroCard({
  macro,
  onEdit,
  onHistory,
}: {
  macro: MacroData;
  onEdit: () => void;
  onHistory: () => void;
}) {
  const updatedAt = parseNoZIsoDatetime(macro.updatedDate);
  const collaboratorCount = macro.macroCollaborators?.length ?? 0;
  const hasHistory = (macro.macroHistories?.length ?? 0) > 0;
  const macroName = macro.macroKey?.trim() || 'untitled';
  // Trim the plaintext preview to a single visible line.
  //
  // **Display-only transformation** (per @samer PR #166 review item 2):
  // `String.prototype.replace` returns a NEW string — it does NOT
  // mutate the source. `macro.macroValueFormatted` is the read-only
  // prop value from React Query's cached `MacroData[]`; the `preview`
  // local variable is what we render. Nothing in this chain writes
  // back to the cache or to the underlying macro object. The original
  // `macroValueFormatted` (with U+FFFC and full whitespace) reaches
  // the editor surface in PR-B intact.
  const preview =
    (macro.macroValueFormatted ?? '')
      .replace(/￼/g, '') // U+FFFC object replacement char (Quill image embed marker)
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120) || '(empty)';

  return (
    <li data-slot="settings-record" className="rounded-md border border-border-card bg-surface-card px-3 py-2.5 shadow-1">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p data-slot="settings-record-title" className="text-sm font-semibold text-text-default truncate">
              {macro.macroKey?.trim() || '(no key)'}
            </p>
            {macro.isGlobal && (
              <StatusChip tone="soft" semantic="success">
                Global
              </StatusChip>
            )}
            {!macro.isGlobal && (
              <StatusChip tone="soft" semantic="neutral">
                Private
              </StatusChip>
            )}
            {collaboratorCount > 0 && (
              <CollaboratorBadge
                count={collaboratorCount}
                collaborators={macro.macroCollaborators}
              />
            )}
          </div>

          <p className="mt-1 text-sm text-text-muted line-clamp-2">
            {preview}
          </p>

          <p className="mt-1 text-xs text-text-hint">
            Owned by{' '}
            <span className="font-semibold">{macro.ownerName || '—'}</span>
            {updatedAt && (
              <>
                {' '}
                · Updated {formatDate(updatedAt)}
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {/* History button — disabled when the macro has no history
              snapshots (a freshly-created macro that hasn't been
              edited yet has macroHistories: []). Disabled, its name is the
              reason, which the app-wide instant tooltip shows on hover and
              focus (the wrap-up actions' pattern for a disabled icon button;
              the reason was a native `title` until the icon pass). */}
          <button
            type="button"
            onClick={onHistory}
            disabled={!hasHistory}
            aria-label={
              hasHistory
                ? `View history for macro ${macroName}`
                : `No history yet: macro ${macroName} hasn’t been edited since it was created`
            }
            className="icon-action"
          >
            <History className="size-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onEdit}
            aria-label={`Edit macro ${macroName}`}
            className="icon-action"
          >
            <Pencil className="size-4" aria-hidden />
          </button>
        </div>
      </div>
    </li>
  );
}

function CollaboratorBadge({
  count,
  collaborators,
}: {
  count: number;
  collaborators: MacroCollaborator[];
}) {
  // Tooltip-style hint: first 3 names + "+N more" if truncated.
  const names = collaborators.slice(0, 3).map((c) => c.collaboratorName);
  const remaining = Math.max(0, count - 3);
  const tooltip =
    names.join(', ') + (remaining > 0 ? ` +${remaining} more` : '');

  return (
    <IconChip icon={<Users className="size-4" />} title={tooltip}>
      {count} {count === 1 ? 'Collab' : 'Collabs'}
    </IconChip>
  );
}
